import React from "react"
import { AttachmentType, ShareAttachmentFragment } from "~/__generated__/graphql"
import { MessageAttachmentLiveSession } from "./message-attachment-live-session"
import { MessageAttachmentPoll } from "./message-attachment-poll"
import { MessageAttachmentQAndA } from "./message-attachment-qa"
import { MessageAttachmentAudio } from "./message-attachment-audio"
import { MessageAttachmentVideo } from "./message-attachment-video"
import { MessageAttachmentTrivia } from "./message-attachment-trivia"
import { MessageAttachmentEpisode } from "./message-attachment-episode"
import { MessageAttachmentQuote } from "./message-attachment-quote"
import { MessageAttachmentPredictions } from "./message-attachment-predictions"
import { UnsupportedAttachment } from "./unsupported-attachment"
import imageResize from "~/common/image-resize"

interface MessageAttachmentsProps {
  attachments: ShareAttachmentFragment[] | null | undefined
  messageCreatedAt?: string
  className?: string
}

export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  attachments,
  messageCreatedAt,
  className,
}) => {
  if (!attachments || attachments.length === 0) return null

  const attachment = attachments[0]

  switch (attachment.attachmentType) {
    case AttachmentType.Image:
    case AttachmentType.AuraSticker:
      return (
        <img
          src={imageResize(attachment.imageUrl ?? "", {})}
          alt="attachment"
          className={`max-h-[400px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Giphy:
      return (
        <img
          src={attachment.giphy?.data?.images?.original_still?.url}
          alt="attachment"
          className={`max-h-[250px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Poll:
      return attachment.poll ? <MessageAttachmentPoll poll={attachment.poll} /> : null
    case AttachmentType.QAndA:
      return (
        <MessageAttachmentQAndA
          qAndAColor={attachment.qAndAColor}
          qAndAPrompt={attachment.qAndAPrompt}
        />
      )
    case AttachmentType.Audio:
      return <MessageAttachmentAudio />
    case AttachmentType.Video:
      return (
        <MessageAttachmentVideo
          muxPlaybackId={attachment.muxPlaybackId}
          className={`max-h-[250px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Trivia:
      if (!attachment.trivia) return null

      return (
        <MessageAttachmentTrivia
          title={attachment.trivia.title}
          color={attachment.trivia.color}
          responseCount={attachment.trivia.responseCount}
        />
      )
    case AttachmentType.LiveSession:
      if (!attachment.liveSession) return <UnsupportedAttachment />
      return <MessageAttachmentLiveSession liveSession={attachment.liveSession} />
    case AttachmentType.Episode:
      if (!attachment.episode) return <UnsupportedAttachment />
      return <MessageAttachmentEpisode episode={attachment.episode} />
    case AttachmentType.Quote:
      if (!attachment.quoteText) return <UnsupportedAttachment />
      return (
        <MessageAttachmentQuote
          quoteText={attachment.quoteText}
          quoteAuthor={attachment.quoteAuthor}
        />
      )
    case AttachmentType.Predictions:
      if (!attachment.predictions) return <UnsupportedAttachment />
      return (
        <MessageAttachmentPredictions
          predictions={attachment.predictions}
          messageCreatedAt={messageCreatedAt}
        />
      )
    default:
      return <UnsupportedAttachment />
  }
}
